import { gql } from '@apollo/client'
import { gql as contentfulGQL } from '@apollo/client'
import { CONTENT_AVAIL_FRAG } from '../ContentAvailabilityService'

export const GET_FRANCHISE_PAGE_COLLECTION = contentfulGQL/* GraphQL */ `
  query getFranchisePageCollection($slug: String!, $locale: String, $preview: Boolean!) {
    franchisePageCollection(where: { slug: $slug }, locale: $locale, preview: $preview, limit: 1) {
      items {
        slug
        heroBackgroundVideo
        shopifyStore {
          storeUrl
          accessToken
          handle
          storeHref
        }
        posterHref
        posterDesktop {
          altText
          item
        }
        posterTablet {
          altText
          item
        }
        posterMobile {
          altText
          item
        }
        projectsCollection(limit: 5) {
          items {
            slug
            franchiseWatchOrderText
          }
        }
        logoOverride {
          item
        }
      }
    }
  }
`

export const GET_FRANCHISE_BY_SLUG = gql`
  ${CONTENT_AVAIL_FRAG}
  query getFranchiseBySlug($slug: String!, $includePrerelease: Boolean = true) {
    franchiseBySlug(slug: $slug) {
      id
      name
      slug
      description(version: MEDIUM)
      shortDescription: description(version: SHORT)
      images(category: LOGO, aspect: "original") {
        cloudinaryPath
      }
      backdropLandscape: images(category: BACKDROP, aspect: "16:9") {
        cloudinaryPath
      }
      backdropPortrait: images(category: BACKDROP, aspect: "9:16") {
        cloudinaryPath
      }
      projects {
        name
        slug
        theaterDescription
        projectType
        highestScore
        logoCloudinaryPath
        metadata {
          genres
          contentRating
        }
        title {
          __typename
          ... on ContentSeries {
            id
            seasons(first: 20) {
              edges {
                node {
                  id
                  name
                  seasonNumber
                  episodes(first: 100) {
                    edges {
                      node {
                        __typename
                        id
                        name
                        description
                        episodeNumber
                        name
                        posterCloudinaryPath
                        posterLandscapeCloudinaryPath
                        slug
                        subtitle
                        duration
                        watchPosition {
                          id
                          position
                        }
                        ... on Node {
                          id
                        }
                        ... on ContentSharable {
                          isGuildShareAvailable
                        }
                        ... on ContentWatchableAvailability {
                          watchableAvailabilityStatus
                          actionsToWatch {
                            __typename
                          }
                          watchableReasons {
                            __typename
                            ... on LinkshareAvailabilityAction {
                              startDatetime
                            }
                          }
                        }
                        ... on ContentDisplayable {
                          id
                          ratings
                          landscapeNonTitleImage: image(aspect: "16:9", category: NON_TITLE_ART) {
                            aspect
                            category
                            cloudinaryPath
                          }
                          portraitNonTitleImage: image(aspect: "2:3", category: NON_TITLE_ART) {
                            aspect
                            category
                            cloudinaryPath
                          }
                          squareNonTitleImage: image(aspect: "1:1", category: NON_TITLE_ART) {
                            aspect
                            category
                            cloudinaryPath
                          }
                          landscapeBackdrop: image(aspect: "16:9", category: BACKDROP) {
                            aspect
                            category
                            cloudinaryPath
                          }
                          portraitBackdrop: image(aspect: "9:16", category: BACKDROP) {
                            aspect
                            category
                            cloudinaryPath
                          }
                          logo: image(aspect: "9:5", category: LOGO) {
                            aspect
                            category
                            cloudinaryPath
                          }
                        }
                        ...ContentAvailability
                      }
                    }
                  }
                }
              }
            }
          }
          ... on Node {
            id
          }
          ... on ContentSharable {
            isGuildShareAvailable
          }
          ... on ContentWatchableAvailability {
            watchableAvailabilityStatus
            actionsToWatch {
              __typename
            }
            watchableReasons {
              __typename
              ... on LinkshareAvailabilityAction {
                startDatetime
              }
            }
          }
          ... on ContentDisplayable {
            id
            ratings
            genres
            description(version: MEDIUM)
            landscapeNonTitleImage: image(aspect: "16:9", category: NON_TITLE_ART) {
              aspect
              category
              cloudinaryPath
            }
            portraitNonTitleImage: image(aspect: "2:3", category: NON_TITLE_ART) {
              aspect
              category
              cloudinaryPath
            }
            squareNonTitleImage: image(aspect: "1:1", category: NON_TITLE_ART) {
              aspect
              category
              cloudinaryPath
            }
            landscapeBackdrop: image(aspect: "16:9", category: BACKDROP) {
              aspect
              category
              cloudinaryPath
            }
            portraitBackdrop: image(aspect: "9:16", category: BACKDROP) {
              aspect
              category
              cloudinaryPath
            }
            logo: image(aspect: "9:5", category: LOGO) {
              aspect
              category
              cloudinaryPath
            }
          }
        }
        seasons {
          name
          episodes(includePrerelease: $includePrerelease, includePresale: $includePrerelease) {
            name
            description
            posterLandscapeCloudinaryPath
            posterCloudinaryPath
            earlyAccessDate
            publiclyAvailableDate
            activeDate
            unavailableReason
            slug
            guid
            subtitle
            watchPosition {
              id
              position
            }
            source {
              duration
            }
            subtitle
            isTrailer
            seasonNumber
            projectSlug
          }
        }
        franchiseDetails {
          watchSequence
        }
        primaryFlowPhases {
          status
          phaseSlugEnum
          releaseWindows {
            id
            start
            end
            startSpecificity
            region {
              id
              name
            }
          }
        }
        title {
          ... on ContentDisplayable {
            id
            ratings
            landscapeTitleImage: image(category: TITLE_ART, aspect: "16:9") {
              cloudinaryPath
            }
            landscapeAngelImage: image(category: ANGEL_KEY_ART_1, aspect: "16:9") {
              cloudinaryPath
            }
            landscapeAngelImage3: image(category: ANGEL_KEY_ART_3, aspect: "16:9") {
              cloudinaryPath
            }
            landscapeBackdropImage: image(category: BACKDROP, aspect: "16:9") {
              cloudinaryPath
            }
            portraitTitleImage: image(category: TITLE_ART, aspect: "2:3") {
              cloudinaryPath
            }
            portraitAngelImage: image(category: ANGEL_KEY_ART_1, aspect: "2:3") {
              cloudinaryPath
            }
            portraitAngelImage3: image(category: ANGEL_KEY_ART_3, aspect: "2:3") {
              cloudinaryPath
            }
            portraitBackdropImage: image(category: BACKDROP, aspect: "9:16") {
              cloudinaryPath
            }
          }
        }
      }
    }
  }
`

export const GET_LIGHT_FRANCHISE_BY_SLUG = gql`
  query getLightFranchiseBySlug($slug: String!) {
    franchiseBySlug(slug: $slug) {
      id
      name
      slug
      description(version: MEDIUM)
      images(category: ANGEL_KEY_ART_1, aspect: "16:9") {
        cloudinaryPath
      }
      projects {
        id
        name
        slug
        projectType
        theaterDescription
        highestScore
        primaryFlowPhases {
          status
          phaseSlugEnum
          releaseWindows {
            id
            start
            end
            startSpecificity
            region {
              id
              name
            }
          }
        }
        title {
          ... on ContentDisplayable {
            id
            landscapeTitleImage: image(category: TITLE_ART, aspect: "16:9") {
              cloudinaryPath
            }
            landscapeAngelImage: image(category: ANGEL_KEY_ART_1, aspect: "16:9") {
              cloudinaryPath
            }
            landscapeAngelImage3: image(category: ANGEL_KEY_ART_3, aspect: "16:9") {
              cloudinaryPath
            }
            landscapeBackdropImage: image(category: BACKDROP, aspect: "16:9") {
              cloudinaryPath
            }
            portraitTitleImage: image(category: TITLE_ART, aspect: "2:3") {
              cloudinaryPath
            }
            portraitAngelImage: image(category: ANGEL_KEY_ART_1, aspect: "2:3") {
              cloudinaryPath
            }
            portraitAngelImage3: image(category: ANGEL_KEY_ART_3, aspect: "2:3") {
              cloudinaryPath
            }
            portraitBackdropImage: image(category: BACKDROP, aspect: "9:16") {
              cloudinaryPath
            }
          }
        }
      }
    }
  }
`
